import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  createPhoneTrackerMember,
  deletePhoneTrackerMember,
  listPhoneTrackerMembers,
  updatePhoneTrackerMember,
} from "../../../api/adminTrackerApi";

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function safe(v) {
  return v == null ? "" : String(v);
}

function fmtDateTime(v) {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function useDebouncedValue(value, delayMs = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function csvEscape(v) {
  const s = v == null ? "" : String(v);
  if (/[",\n]/.test(s)) return `"${s.replaceAll('"', '""')}"`;
  return s;
}

function downloadCsv(filename, rows) {
  const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function Chip({ tone = "slate", children }) {
  const cls =
    tone === "green"
      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
      : tone === "amber"
      ? "bg-amber-50 text-amber-900 border-amber-200"
      : tone === "blue"
      ? "bg-sky-50 text-sky-900 border-sky-200"
      : "bg-slate-50 text-slate-700 border-slate-200";

  return (
    <span className={classNames("inline-flex items-center rounded-md border px-2.5 py-1 text-[11px] font-semibold", cls)}>
      {children}
    </span>
  );
}

function KpiCard({ title, value, hint, tone = "slate" }) {
  const ring =
    tone === "green"
      ? "ring-emerald-100"
      : tone === "amber"
      ? "ring-amber-100"
      : tone === "blue"
      ? "ring-sky-100"
      : "ring-slate-100";

  return (
    <div className={classNames("rounded-xl border border-slate-200 bg-white px-4 py-3 ring-4", ring)}>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{title}</div>
      <div className="mt-1 text-2xl font-semibold text-slate-900">{value}</div>
      {hint ? <div className="mt-1 text-xs text-slate-500">{hint}</div> : null}
    </div>
  );
}

function membershipTone(v) {
  const x = safe(v).toUpperCase();
  if (x === "BOTH") return "green";
  if (x === "WHATSAPP_ONLY") return "amber";
  if (x === "WAYROOT_ONLY") return "blue";
  return "slate";
}

function membershipLabel(v) {
  return safe(v).replaceAll("_", " ") || "-";
}

function PhoneTrackerModal({ open, initial, saving, error, onClose, onSubmit }) {
  const [form, setForm] = useState({
    phoneNumber: "",
    name: "",
    whatsappMember: true,
    wayrootAppUser: false,
    notes: "",
  });

  useEffect(() => {
    if (!open) return;
    setForm({
      phoneNumber: initial?.phoneNumber || "",
      name: initial?.name || "",
      whatsappMember: initial?.whatsappMember ?? true,
      wayrootAppUser: initial?.wayrootAppUser ?? false,
      notes: initial?.notes || "",
    });
  }, [open, initial]);

  if (!open) return null;

  function patch(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function submit(e) {
    e.preventDefault();
    onSubmit(form);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 px-4 py-6 sm:items-center">
      <div className="w-full max-w-2xl rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <div className="text-lg font-bold text-slate-900">{initial?.id ? "Edit phone tracker" : "Add phone tracker"}</div>
            <div className="mt-1 text-sm text-slate-500">Keep migration tracking between WhatsApp group and the Wayroot app in one place.</div>
          </div>
          <button onClick={onClose} className="rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            Close
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4 px-6 py-5">
          <div className="grid gap-4 md:grid-cols-2">
            <label className="block">
              <div className="text-xs font-semibold text-slate-600">Phone number *</div>
              <input
                value={form.phoneNumber}
                onChange={(e) => patch("phoneNumber", e.target.value)}
                required
                className="mt-2 h-11 w-full rounded-md border border-slate-200 px-3 text-sm text-slate-900"
              />
            </label>

            <label className="block">
              <div className="text-xs font-semibold text-slate-600">Name</div>
              <input
                value={form.name}
                onChange={(e) => patch("name", e.target.value)}
                className="mt-2 h-11 w-full rounded-md border border-slate-200 px-3 text-sm text-slate-900"
              />
            </label>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3">
              <input type="checkbox" checked={!!form.whatsappMember} onChange={(e) => patch("whatsappMember", e.target.checked)} />
              <div>
                <div className="text-sm font-semibold text-slate-900">WhatsApp member</div>
                <div className="text-xs text-slate-500">Mark if the number is part of your WhatsApp group.</div>
              </div>
            </label>

            <label className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3">
              <input type="checkbox" checked={!!form.wayrootAppUser} onChange={(e) => patch("wayrootAppUser", e.target.checked)} />
              <div>
                <div className="text-sm font-semibold text-slate-900">Wayroot app user</div>
                <div className="text-xs text-slate-500">Mark if they have moved to the Wayroot app.</div>
              </div>
            </label>
          </div>

          <label className="block">
            <div className="text-xs font-semibold text-slate-600">Notes</div>
            <textarea
              value={form.notes}
              onChange={(e) => patch("notes", e.target.value)}
              rows={4}
              className="mt-2 w-full rounded-md border border-slate-200 px-3 py-3 text-sm text-slate-900"
            />
          </label>

          {error ? <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">{error}</div> : null}

          <div className="flex items-center justify-end gap-3">
            <button type="button" onClick={onClose} className="rounded-md border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="rounded-md bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50">
              {saving ? "Saving..." : initial?.id ? "Save changes" : "Create entry"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminPhoneTrackerPage() {
  const [q, setQ] = useState("");
  const qDebounced = useDebouncedValue(q, 350);
  const [membershipStatus, setMembershipStatus] = useState("ALL");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalInitial, setModalInitial] = useState(null);
  const [modalError, setModalError] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchSeq = useRef(0);

  useEffect(() => {
    setPage(0);
  }, [qDebounced, membershipStatus, size]);

  const load = useCallback(async () => {
    const seq = ++fetchSeq.current;
    setLoading(true);
    setError("");
    try {
      const resp = await listPhoneTrackerMembers({
        q: qDebounced,
        membershipStatus: membershipStatus === "ALL" ? undefined : membershipStatus,
        page,
        size,
      });
      if (seq !== fetchSeq.current) return;
      setData(resp || null);
    } catch (e) {
      if (seq !== fetchSeq.current) return;
      setError(e?.message || "Failed to load phone tracker");
      setData(null);
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  }, [membershipStatus, page, qDebounced, size]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = data?.content || [];
  const totalPages = Number(data?.totalPages || 0);

  const summary = data?.summary;
  const summaryCards = [
    { title: "Total Entries", value: Number(summary?.totalEntries || 0).toLocaleString("en-IN"), hint: "Tracked phone numbers", tone: "blue" },
    { title: "WhatsApp Only", value: Number(summary?.whatsappOnly || 0).toLocaleString("en-IN"), hint: "Not yet marked as app users", tone: "amber" },
    { title: "Wayroot Only", value: Number(summary?.wayrootOnly || 0).toLocaleString("en-IN"), hint: "App users outside WhatsApp list", tone: "blue" },
    { title: "Both", value: Number(summary?.both || 0).toLocaleString("en-IN"), hint: "WhatsApp + app", tone: "green" },
    { title: "Neither", value: Number(summary?.neither || 0).toLocaleString("en-IN"), hint: "Needs classification", tone: "amber" },
    { title: "Matched In Platform", value: Number(summary?.matchedInPlatform || 0).toLocaleString("en-IN"), hint: "User/owner/driver match in DB", tone: "green" },
  ];

  function openCreate() {
    setModalInitial(null);
    setModalError("");
    setModalOpen(true);
  }

  function openEdit(row) {
    setModalInitial(row);
    setModalError("");
    setModalOpen(true);
  }

  async function submitModal(payload) {
    setSaving(true);
    setModalError("");
    try {
      if (modalInitial?.id) {
        await updatePhoneTrackerMember(modalInitial.id, payload);
      } else {
        await createPhoneTrackerMember(payload);
      }
      setModalOpen(false);
      setModalInitial(null);
      await load();
    } catch (e) {
      setModalError(e?.message || "Failed to save phone tracker");
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(row) {
    const ok = window.confirm(`Delete phone tracker entry for ${row.phoneNumber || "this number"}?`);
    if (!ok) return;
    try {
      await deletePhoneTrackerMember(row.id);
      await load();
    } catch (e) {
      window.alert(e?.message || "Failed to delete phone tracker entry");
    }
  }

  async function exportCsv() {
    try {
      const resp = await listPhoneTrackerMembers({
        q: qDebounced,
        membershipStatus: membershipStatus === "ALL" ? undefined : membershipStatus,
        page: 0,
        size: 500,
      });
      const items = resp?.content || [];
      const lines = [
        ["Phone Number", "Name", "WhatsApp Member", "Wayroot App User", "Membership Status", "User Match", "Owner Match", "Driver Match", "Notes", "Updated At"]
          .map(csvEscape)
          .join(","),
      ];
      items.forEach((row) => {
        lines.push(
          [
            row.phoneNumber,
            row.name,
            row.whatsappMember,
            row.wayrootAppUser,
            row.membershipStatus,
            row.userMatch,
            row.ownerMatch,
            row.driverMatch,
            row.notes,
            row.updatedAt,
          ].map(csvEscape).join(",")
        );
      });
      const stamp = new Date().toISOString().slice(0, 19).replaceAll(":", "-");
      downloadCsv(`phone-tracker-${stamp}.csv`, lines);
    } catch (e) {
      window.alert(e?.message || "Failed to export phone tracker");
    }
  }

  return (
    <div className="min-w-0 space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-6 py-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Phone Tracker</div>
              <div className="mt-1 text-sm text-slate-600">
                Manage WhatsApp-member and Wayroot-app migration tracking with live DB matching for users, owners, and drivers.
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Chip tone="blue">EDITABLE</Chip>
              <Chip tone="green">PHONE FIRST</Chip>
              <Chip>EXPORTABLE</Chip>
            </div>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-2 2xl:grid-cols-3">
            {summaryCards.map((item) => (
              <KpiCard key={item.title} {...item} />
            ))}
          </div>
        </div>

        <div className="border-b border-slate-200 bg-slate-50/50 px-6 py-4">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
            <div className="grid gap-3 md:grid-cols-2 2xl:flex 2xl:flex-wrap 2xl:items-end">
              <label className="block w-full 2xl:min-w-[280px] 2xl:flex-1">
                <div className="text-xs font-semibold text-slate-600">Search</div>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Phone / name / notes"
                  className="mt-2 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900"
                />
              </label>

              <label className="block">
                <div className="text-xs font-semibold text-slate-600">Membership status</div>
                <select value={membershipStatus} onChange={(e) => setMembershipStatus(e.target.value)} className="mt-2 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900">
                  <option value="ALL">All</option>
                  <option value="WHATSAPP_ONLY">WhatsApp only</option>
                  <option value="WAYROOT_ONLY">Wayroot only</option>
                  <option value="BOTH">Both</option>
                  <option value="NEITHER">Neither</option>
                </select>
              </label>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between xl:justify-end">
              <button onClick={exportCsv} className="rounded-md border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 hover:bg-slate-50">
                Export CSV
              </button>
              <button onClick={openCreate} className="rounded-md bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800">
                Add phone
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[1280px] w-full">
            <thead className="bg-white">
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-6 py-3">Phone</th>
                <th className="px-6 py-3">Name</th>
                <th className="px-6 py-3">Membership</th>
                <th className="px-6 py-3">WhatsApp</th>
                <th className="px-6 py-3">Wayroot</th>
                <th className="px-6 py-3">User match</th>
                <th className="px-6 py-3">Owner match</th>
                <th className="px-6 py-3">Driver match</th>
                <th className="px-6 py-3">Notes</th>
                <th className="px-6 py-3">Updated</th>
                <th className="px-6 py-3">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200">
              {loading ? (
                Array.from({ length: 8 }).map((_, idx) => (
                  <tr key={idx} className="animate-pulse">
                    {Array.from({ length: 11 }).map((__, col) => (
                      <td key={col} className="px-6 py-4">
                        <div className="h-4 rounded bg-slate-100" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : error ? (
                <tr>
                  <td colSpan={11} className="px-6 py-8">
                    <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">{error}</div>
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-6 py-10 text-sm text-slate-600">No phone tracker entries found.</td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="align-top hover:bg-slate-50">
                    <td className="px-6 py-4 text-sm font-semibold text-slate-900 whitespace-nowrap">{row.phoneNumber || "-"}</td>
                    <td className="px-6 py-4 text-sm text-slate-700 whitespace-nowrap">{row.name || "-"}</td>
                    <td className="px-6 py-4 whitespace-nowrap"><Chip tone={membershipTone(row.membershipStatus)}>{membershipLabel(row.membershipStatus)}</Chip></td>
                    <td className="px-6 py-4 whitespace-nowrap"><Chip tone={row.whatsappMember ? "green" : "slate"}>{row.whatsappMember ? "Yes" : "No"}</Chip></td>
                    <td className="px-6 py-4 whitespace-nowrap"><Chip tone={row.wayrootAppUser ? "green" : "slate"}>{row.wayrootAppUser ? "Yes" : "No"}</Chip></td>
                    <td className="px-6 py-4 whitespace-nowrap"><Chip tone={row.userMatch ? "green" : "slate"}>{row.userMatch ? "Matched" : "No"}</Chip></td>
                    <td className="px-6 py-4 whitespace-nowrap"><Chip tone={row.ownerMatch ? "green" : "slate"}>{row.ownerMatch ? "Matched" : "No"}</Chip></td>
                    <td className="px-6 py-4 whitespace-nowrap"><Chip tone={row.driverMatch ? "green" : "slate"}>{row.driverMatch ? "Matched" : "No"}</Chip></td>
                    <td className="px-6 py-4 max-w-[320px] text-sm text-slate-700">{row.notes || "-"}</td>
                    <td className="px-6 py-4 text-sm text-slate-700 whitespace-nowrap">{fmtDateTime(row.updatedAt)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-wrap items-center gap-2">
                        <button onClick={() => openEdit(row)} className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-50">
                          Edit
                        </button>
                        <button onClick={() => onDelete(row)} className="rounded-md border border-rose-200 bg-white px-3 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-slate-200 bg-white px-6 py-4 md:flex-row md:items-center md:justify-between">
          <div className="text-sm text-slate-500">
            Showing <span className="font-semibold text-slate-700">{rows.length}</span> of{" "}
            <span className="font-semibold text-slate-700">{Number(data?.totalElements || 0).toLocaleString("en-IN")}</span>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <select value={size} onChange={(e) => setSize(Number(e.target.value))} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900">
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>{n} / page</option>
              ))}
            </select>

            <div className="text-sm text-slate-700">
              Page <span className="font-semibold">{page + 1}</span> of <span className="font-semibold">{Math.max(1, totalPages)}</span>
            </div>

            <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page <= 0} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-50 disabled:opacity-50">
              Prev
            </button>
            <button onClick={() => setPage((p) => (p + 1 < totalPages ? p + 1 : p))} disabled={page + 1 >= totalPages} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-50 disabled:opacity-50">
              Next
            </button>
          </div>
        </div>
      </div>

      <PhoneTrackerModal
        open={modalOpen}
        initial={modalInitial}
        saving={saving}
        error={modalError}
        onClose={() => {
          if (saving) return;
          setModalOpen(false);
          setModalInitial(null);
          setModalError("");
        }}
        onSubmit={submitModal}
      />
    </div>
  );
}
